import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ScrollableContentProps {
  maxHeight: number | string
  children: React.ReactNode
  className?: string
  contentClassName?: string
}

export function ScrollableContent({ maxHeight, children, className, contentClassName }: ScrollableContentProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [canScrollDown, setCanScrollDown] = useState(false)

  const checkScroll = useCallback(() => {
    const el = scrollContainerRef.current
    if (!el) return
    
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight
    setCanScrollDown((prev) => remaining > (prev ? 1 : 4))
  }, [])

  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return

    checkScroll()
    el.addEventListener('scroll', checkScroll)

    const observer = new ResizeObserver(checkScroll)
    observer.observe(el)

    return () => {
      el.removeEventListener('scroll', checkScroll)
      observer.disconnect()
    }
  }, [checkScroll])

  return (
    <div
      className={cn('flex flex-col rounded-md border border-border bg-background', className)}
      style={{ maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight }}
    >
      <div
        ref={scrollContainerRef}
        className={cn('flex-1 overflow-y-auto min-h-0 p-4', contentClassName)}
      >
        {children}
      </div>
      {canScrollDown && (
        <div
          className="flex items-center justify-end gap-1.5 px-3 py-1 border-t border-border text-xs text-muted-foreground shrink-0"
          aria-hidden="true"
        >
          <ChevronDown className="h-3 w-3" />
          <span>Scroll to read more</span>
        </div>
      )}
    </div>
  )
}
